import * as React from 'react';
import type {DateRange} from 'react-day-picker';
import {
  autoFormatDateInput,
  getMaxDigits,
  parseDateInputFormat,
} from '../utils/inputFormat';

type UseRangeTextInputsParams = {
  value: DateRange | undefined;
  format: (date: Date) => string;
  parse: (text: string) => Date | undefined;
  displayFormat: string;
  onCommit: (next: DateRange | undefined) => void;
  onBlur?: (value: DateRange | undefined) => void;
  onFromComplete?: () => void;
  onToComplete?: () => void;
};

const countDigits = (text: string) => text.replace(/\D/g, '').length;

export function useRangeTextInputs({
  value,
  format,
  parse,
  displayFormat,
  onCommit,
  onBlur,
  onFromComplete,
  onToComplete,
}: UseRangeTextInputsParams) {
  const tokens = React.useMemo(
    () => parseDateInputFormat(displayFormat),
    [displayFormat],
  );
  const maxDigits = React.useMemo(() => getMaxDigits(tokens), [tokens]);

  const [fromText, setFromText] = React.useState(value?.from ? format(value.from) : '');
  const [toText, setToText] = React.useState(value?.to ? format(value.to) : '');

  React.useEffect(() => {
    setFromText(value?.from ? format(value.from) : '');
    setToText(value?.to ? format(value.to) : '');
  }, [format, value?.from, value?.to]);

  const handleFromChange = React.useCallback(
    (raw: string) => {
      const formatted = autoFormatDateInput(raw, tokens);
      const wasComplete = countDigits(fromText) === maxDigits;
      const isComplete = countDigits(formatted) === maxDigits;
      setFromText(formatted);
      if (!wasComplete && isComplete && parse(formatted)) {
        // Defer so the state update is committed before focus shifts —
        // otherwise the blur handler reads the previous (stale) fromText.
        setTimeout(() => onFromComplete?.(), 0);
      }
    },
    [fromText, maxDigits, onFromComplete, parse, tokens],
  );

  const handleToChange = React.useCallback(
    (raw: string) => {
      const formatted = autoFormatDateInput(raw, tokens);
      const wasComplete = countDigits(toText) === maxDigits;
      const isComplete = countDigits(formatted) === maxDigits;
      setToText(formatted);
      if (!wasComplete && isComplete && parse(formatted)) {
        setTimeout(() => onToComplete?.(), 0);
      }
    },
    [maxDigits, onToComplete, parse, toText, tokens],
  );

  const handleFromBlur = React.useCallback(() => {
    if (!fromText) {
      const next = {from: undefined, to: value?.to};
      onCommit(next);
      onBlur?.(next);
      return undefined;
    }
    const parsed = parse(fromText);
    if (parsed) {
      const next = {from: parsed, to: value?.to};
      onCommit(next);
      onBlur?.(next);
      return parsed;
    }
    setFromText(value?.from ? format(value.from) : '');
    return undefined;
  }, [format, fromText, onBlur, onCommit, parse, value]);

  const handleToBlur = React.useCallback(() => {
    if (!toText) {
      const next = {from: value?.from, to: undefined};
      onCommit(next);
      onBlur?.(next);
      return;
    }
    const parsed = parse(toText);
    if (parsed) {
      const next = {from: value?.from, to: parsed};
      onCommit(next);
      onBlur?.(next);
      return;
    }
    setToText(value?.to ? format(value.to) : '');
  }, [format, onBlur, onCommit, parse, toText, value]);

  return {
    fromText,
    toText,
    setFromText,
    setToText,
    handleFromChange,
    handleToChange,
    handleFromBlur,
    handleToBlur,
  };
}
